import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  styleguideColors,
  styleguideColorsExtended,
} from '../../../core/tokens/styleguide';

export interface WorkspaceLayoutProps {
  /** Left pane content — typically the scope sidebar / task list. */
  sidebar: ReactNode;
  /** Work surface header content — typically mode tabs. */
  workHeader?: ReactNode;
  /** Work surface body content. */
  children?: ReactNode;
  /** Initial sidebar width as a fraction of the layout (0–1). Default 0.38. */
  sidebarFraction?: number;
  /** Minimum sidebar width in pixels. Default 360. */
  minSidebarPx?: number;
  /** Maximum sidebar width as a fraction of the layout (0–1). Default 0.60. */
  maxSidebarFraction?: number;
  /** Show the drag-to-resize handle. Default true. */
  resizable?: boolean;
  /** Called continuously while the user drags the handle. */
  onResize?: (fraction: number) => void;
  style?: CSSProperties;
}

const handleStyle: CSSProperties = {
  flex: '0 0 auto',
  width: 9,
  margin: '0 -4px',
  cursor: 'col-resize',
  position: 'relative',
  zIndex: 30,
  background: 'transparent',
  border: 0,
  padding: 0,
  appearance: 'none',
};

const gripStyle: CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 13,
  height: 40,
  background: styleguideColors.white,
  border: `1px solid ${styleguideColors.stroke}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
};

const gridBackground = `linear-gradient(rgba(26,26,26,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(26,26,26,0.04) 1px, transparent 1px)`;

export const WorkspaceLayout = ({
  sidebar,
  workHeader,
  children,
  sidebarFraction = 0.38,
  minSidebarPx = 360,
  maxSidebarFraction = 0.60,
  resizable = true,
  onResize,
  style,
}: WorkspaceLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const [fraction, setFraction] = useState(sidebarFraction);
  const [dragging, setDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const layout = layoutRef.current;
    if (!layout) return;

    const onMove = (e: MouseEvent) => {
      const rect = layout.getBoundingClientRect();
      let newFraction = (e.clientX - rect.left) / rect.width;
      const minFraction = minSidebarPx / rect.width;
      newFraction = Math.max(minFraction, Math.min(maxSidebarFraction, newFraction));
      setFraction(newFraction);
      onResize?.(newFraction);
    };
    const onUp = () => setDragging(false);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragging, minSidebarPx, maxSidebarFraction, onResize]);

  const sidebarWidth = `${(fraction * 100).toFixed(2)}%`;

  return (
    <div
      ref={layoutRef}
      data-workspace-layout='true'
      style={{
        flex: 1,
        display: 'flex',
        minHeight: 0,
        overflow: 'hidden',
        ...style,
      }}
    >
      <aside
        data-workspace-sidebar='true'
        style={{
          flex: `0 0 ${sidebarWidth}`,
          minWidth: minSidebarPx,
          maxWidth: `${(maxSidebarFraction * 100).toFixed(2)}%`,
          display: 'flex',
          flexDirection: 'column',
          background: styleguideColors.white,
          overflow: 'hidden',
          borderRight: `1px solid ${styleguideColors.stroke}`,
        }}
      >
        {sidebar}
      </aside>

      {resizable
        ? (
          <button
            type='button'
            data-workspace-handle='true'
            onMouseDown={handleMouseDown}
            aria-label='Resize sidebar'
            style={{
              ...handleStyle,
              cursor: dragging ? 'col-resize' : 'col-resize',
            }}
          >
            <span style={gripStyle}>
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  style={{
                    width: 3,
                    height: 3,
                    borderRadius: '50%',
                    background: styleguideColorsExtended.textTertiary,
                  }}
                />
              ))}
            </span>
          </button>
        )
        : null}

      <section
        data-workspace-work='true'
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          background: styleguideColors.paper,
          overflow: 'hidden',
        }}
      >
        {workHeader
          ? (
            <div
              data-workspace-work-header='true'
              style={{
                height: 44,
                background: styleguideColors.white,
                borderBottom: `1px solid ${styleguideColors.stroke}`,
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px',
                gap: 2,
                flexShrink: 0,
              }}
            >
              {workHeader}
            </div>
          )
          : null}
        <div
          data-workspace-work-body='true'
          style={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            position: 'relative',
            backgroundColor: styleguideColors.paper,
            backgroundImage: gridBackground,
            backgroundSize: '24px 24px',
          }}
        >
          {children}
        </div>
      </section>
    </div>
  );
};
